import { Button, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { CloseButton } from '@/component/Button/Buttons';
import IconButton from '@/component/Button/IconButton';
import { CardImage } from '@/component/Cards/CardImage';
import { getOverlayIconButtonStyle } from '@/component/Image/ImageModal';
import { Skeleton } from '@/component/Skeleton';
import Translate from '@/component/Translate';
import { ConversationReferenceDto } from '@/feature/chatbot/api.types';
import { useReferenceImageUrl } from '@/feature/chatbot/useChatReference';
import { useTexts } from '@/feature/lang';
import { Icon } from '@/icon/Icon';
import { openInNewTab } from '@/util/window';
import {
  ReferencePagination,
  useReferencePagination,
} from './ReferencePagination';

const ActionsContainer = styled('div', {
  name: 'ChatbotReferenceImage',
  slot: 'ActionsContainer',
})(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  margin: theme.spacing(1),
  display: 'flex',
  gap: theme.spacing(1),
  zIndex: 1,
}));

const OverlayIconButton = styled(IconButton, {
  name: 'ChatbotReferenceImage',
  slot: 'ImageOverlayIconButton',
})(({ theme }) => ({ ...getOverlayIconButtonStyle(theme) }));

const ImageFooter = styled('div', {
  name: 'ChatbotReferenceImage',
  slot: 'ImageFooter',
})(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  margin: theme.spacing(1),
  zIndex: 1,
  backgroundColor:
    theme.palette.mode === 'light' ? 'rgba(255,255,255,.5)' : 'rgba(0,0,0,.4)',
  borderRadius: '18px',
  width: 'calc(100% - 16px)',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${theme.palette.divider}`,
}));

const SkeletonContainer = styled('div', {
  name: 'ChatbotReferenceImage',
  slot: 'SkeletonContainer',
})(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: 'calc(100% - 30px)',
  height: 'calc(100% - 30px)',
  margin: '15px',
  backgroundColor: theme.palette.background.paper,
}));

const ModalImageFooter = styled('div', {
  name: 'ChatbotReferenceImage',
  slot: 'ModalImageFooter',
})(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
}));

const ModalPaginationContainer = styled('div', {
  name: 'ChatbotReferenceImage',
  slot: 'ModalPaginationContainer',
})({
  display: 'flex',
  justifyContent: 'center',
  '& button[disabled]': { color: 'rgba(0, 0, 0, 0.26)' },
  '& button:hover': { backgroundColor: 'rgba(0, 0, 0, 0.15)' },
});

const ModalPaginationBackground = styled('div', {
  name: 'ChatbotReferenceImage',
  slot: 'ModalPaginationBackground',
})(({ theme }) => ({
  color: theme.palette.grey[900],
  display: 'flex',
  alignItems: 'center',
  borderRadius: '18px',
  backgroundColor: 'rgba(255,255,255,.5)',
}));

const ModalButtonsContainer = styled('div', {
  name: 'ChatbotReferenceImage',
  slot: 'ModalButtonsContainer',
})(({ theme }) => ({
  display: 'flex',
  justifyContent: 'right',
  gap: theme.spacing(1),
}));

const OverlayCloseButton = styled(CloseButton, {
  name: 'ChatbotReferenceImage',
  slot: 'OverlayCloseButton',
})(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  '&:hover': { backgroundColor: theme.palette.getGreyOpaque(30) },
}));

export const ReferenceCardImage = ({
  reference: {
    documentName,
    documentId,
    topicId,
    pages,
    documentUrl,
    mostRelevantPage,
  },
}: {
  reference: ConversationReferenceDto;
}) => {
  const texts = useTexts().component.chatbot;
  const [open, setOpen] = useState(false);
  const paginationProps = useReferencePagination(pages, mostRelevantPage);

  const { data: url, isLoading } = useReferenceImageUrl({
    documentId,
    topicId,
    pageNumber: paginationProps.pageNumber,
    dpi: 150,
  });
  const stickyUrl = useStickyUrl(url);
  const pdfUrl = `${documentUrl}#page=${paginationProps.pageNumber}`;
  const openPdf = () => openInNewTab(pdfUrl);

  return (
    <>
      <ActionsContainer>
        <OverlayIconButton size="small" onClick={openPdf}>
          <Icon name="open_in_new" size="small" />
        </OverlayIconButton>
        <OverlayIconButton size="small" onClick={() => setOpen(true)}>
          <Icon name="zoom_in" size="small" />
        </OverlayIconButton>
      </ActionsContainer>
      <ImageFooter>
        <ReferencePagination {...paginationProps} />
      </ImageFooter>
      <CardImage
        sx={{ width: '100%' }}
        src={stickyUrl}
        alt={documentName}
        size="original"
        open={open}
        setOpen={setOpen}
        modal={Boolean(stickyUrl)}
        isLoading={isLoading}
        modalOverlay={
          <>
            {isLoading && (
              <SkeletonContainer>
                <Skeleton width="100%" height="100%" variant="rectangular" />
              </SkeletonContainer>
            )}
            <ModalImageFooter>
              <ModalPaginationContainer>
                <ModalPaginationBackground>
                  <ReferencePagination {...paginationProps} />
                </ModalPaginationBackground>
              </ModalPaginationContainer>
              <ModalButtonsContainer>
                <OverlayCloseButton onClick={() => setOpen(false)} />
                <Button onClick={openPdf} variant="contained" color="primary">
                  <Translate text={texts.reference.openInNewTab} />
                </Button>
              </ModalButtonsContainer>
            </ModalImageFooter>
          </>
        }
      />
    </>
  );
};

const useStickyUrl = (url?: string) => {
  // We need to persist the image url of the last viewed page, as it is
  // needed for sizing the modal image, while loading the next page.
  // The size of the next page is actually unknown, but a good guess is that
  // it has the same size as the previous page.
  const [stickyUrl, setUrl] = useState('');
  useEffect(() => {
    if (url) {
      setUrl(url);
    }
  }, [url]);

  return stickyUrl;
};
